/**
 * Authenticated layout wrapper for React Router.
 * SECURITY: Auth is enforced by loaders, not this wrapper.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { observer } from "mobx-react-lite";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { Lock } from "lucide-react";
import AuthenticatedLayout from "@/components/Layout/AuthenticatedLayout";
import useStore from "@/hooks/useStore";
import { useActingUserChange, useNotification } from "@/hooks";
import DefaultStore from "@/store/Store";
import OpportunitiesBackgroundSync from "@/store/OpportunitiesBackgroundSync";
import PropertyBackgroundSync from "@/store/PropertyBackgroundSync";
import AddedPropertyProcessBridge from "@/store/AddedPropertyProcessBridge";
import AddedPropertyReportsWatcherSync from "@/store/AddedPropertyReportsWatcherSync";
import { canAccessPropertiesNav } from "@/components/Sidebar/navRoleAccess";
import { identifyUserflowFromAuth } from "@/utils/userflow";
import RoleReconciliationStore from "@/store/RoleReconciliationStore";
import { capitalize } from "@/utils/stringUtils";
import { getDefaultRoute } from "./loaders";

/** Authenticated layout with sign out, customer switching, and sidebar state. */
export const AuthenticatedLayoutWrapper = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const store = useStore();
  const { authStore } = store;
  const { notify } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [roleReconciliationStore] = useState(() => new RoleReconciliationStore());

  // SECURITY: On customer change, reset property domain stores and warm cache for new customer.
  // PropertyBackgroundSync only runs when the active view can access Properties (same rule as sidebar nav).
  // start() is idempotent; runs here (after first paint) instead of in the route loader so LCP is not blocked.
  useEffect(() => {
    const syncProperties = canAccessPropertiesNav(authStore.role);
    if (syncProperties) {
      PropertyBackgroundSync.start(DefaultStore);
      PropertyBackgroundSync.setActiveCustomer(
        authStore.customer ?? null,
        authStore.userId ?? null,
        authStore.currentRole?.() ?? authStore.role ?? null
      );
    } else {
      PropertyBackgroundSync.stop();
    }
    OpportunitiesBackgroundSync.start(DefaultStore);
    OpportunitiesBackgroundSync.setSession({
      customer: authStore.customer ?? null,
      userEmail: authStore.email ?? null,
    });
    AddedPropertyReportsWatcherSync.start(DefaultStore);
    AddedPropertyReportsWatcherSync.setActiveCustomer(authStore.customer ?? null);
    AddedPropertyProcessBridge.start(DefaultStore);
    DefaultStore.syncProcessTrackerPersistence();
    return () => {
      PropertyBackgroundSync.stop();
      OpportunitiesBackgroundSync.stop();
      AddedPropertyReportsWatcherSync.stop();
      AddedPropertyProcessBridge.stop();
    };
  }, [authStore.customer, authStore.userId, authStore.email, authStore.role]);

  useEffect(() => {
    AddedPropertyReportsWatcherSync.setNotify(notify);
    return () => {
      AddedPropertyReportsWatcherSync.setNotify(null);
    };
  }, [notify]);

  // Identify the authenticated user with Userflow; re-runs on customer/identity change,
  // once the profile (name, signed_up_at) finishes loading, and when accounts load (group attrs).
  useEffect(() => {
    identifyUserflowFromAuth(authStore);
  }, [authStore, authStore.userId, authStore.customer, authStore.userProfileLoaded, authStore.accounts]);

  const handleSignOut = useCallback(() => {
    store.signOut().then(() => navigate("/signin"));
  }, [store, navigate]);

  const handleCustomerChange = useCallback(
    async (newCustomer) => {
      const customerId = newCustomer == null ? "" : newCustomer;
      await authStore.stopImpersonation();
      authStore.setCustomer(customerId);
      // SECURITY: Notify root store so all domain stores reset/flush on customer change (CWE-501).
      DefaultStore.onCustomerChange(customerId);
      // SECURITY: Always redirect to default route on customer change so no previous-customer data/URL remains (CWE-501).
      navigate(getDefaultRoute(authStore), { replace: true });
      void Promise.all([authStore.fetchFiles(), authStore.fetchAgents()]).catch(() => {});
    },
    [authStore, navigate]
  );

  // HAI-1096: role ("view") switch → reset domain stores and land on the new role's default route.
  // Mount capture prevents redirecting deep links on first render (bootstrap already resolved the default).
  const activeRoleRef = useRef(null);
  const { handleActingUserChange: applyActingUserChange, skipNextActiveRoleIdentityChangeRef } =
    useActingUserChange();

  const handleActingUserChange = useCallback(
    async (selection) => {
      await applyActingUserChange(selection);
      activeRoleRef.current = String(authStore.activeRole ?? "").trim();
    },
    [applyActingUserChange, authStore]
  );
  useEffect(() => {
    const nextRole = String(authStore.activeRole ?? "").trim();
    if (activeRoleRef.current === null) {
      activeRoleRef.current = nextRole;
      return;
    }
    if (nextRole === activeRoleRef.current) return;
    if (skipNextActiveRoleIdentityChangeRef.current) {
      skipNextActiveRoleIdentityChangeRef.current = false;
      activeRoleRef.current = nextRole;
      return;
    }
    activeRoleRef.current = nextRole;
    DefaultStore.onActiveRoleChange();
    navigate(getDefaultRoute(authStore), { replace: true });
  }, [authStore.activeRole, authStore, navigate]);

  useEffect(() => {
    const evaluateRoleMismatch = () => {
      roleReconciliationStore.evaluatePathOnFocus({
        pathname: location.pathname,
        authStore,
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        evaluateRoleMismatch();
      }
    };

    window.addEventListener("focus", evaluateRoleMismatch);
    document.addEventListener("visibilitychange", onVisibilityChange);
    evaluateRoleMismatch();
    return () => {
      window.removeEventListener("focus", evaluateRoleMismatch);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [authStore, location.pathname, roleReconciliationStore]);

  const roleLabel = useCallback((role) => {
    const normalized = String(role ?? "")
      .trim()
      .toLowerCase();
    if (!normalized) return "current role";
    if (normalized === "realtor") return "Real Estate Agent";
    return capitalize(normalized, true);
  }, []);

  const handleRoleDialogSelect = useCallback(
    (selection) => {
      const result = roleReconciliationStore.resolveSelection(selection, authStore);
      if (!result) return;

      const selectedRole = String(result.selectedRole ?? "")
        .trim()
        .toLowerCase();
      if (!selectedRole) {
        roleReconciliationStore.dismissDialog();
        return;
      }

      const activeRole = String(authStore.activeRole ?? authStore.role ?? "")
        .trim()
        .toLowerCase();
      const roleChanged = selectedRole !== activeRole;

      if (roleChanged) {
        skipNextActiveRoleIdentityChangeRef.current = true;
      }
      authStore.setActiveRole(selectedRole);
      if (roleChanged) {
        DefaultStore.onActiveRoleChange();
      }

      roleReconciliationStore.dismissDialog();

      if (!result.canStayOnPage) {
        navigate(getDefaultRoute(authStore), { replace: true });
      }
    },
    [authStore, navigate, roleReconciliationStore, skipNextActiveRoleIdentityChangeRef]
  );

  return (
    <>
      <AuthenticatedLayout
        onCustomerChange={handleCustomerChange}
        onActingUserChange={handleActingUserChange}
        onSignOut={handleSignOut}
        onSidebarCollapseChange={setIsSidebarCollapsed}
        isSidebarCollapsed={isSidebarCollapsed}
      />
      <Dialog
        open={roleReconciliationStore.isDialogOpen}
        onClose={() => roleReconciliationStore.closeDialog()}
        aria-labelledby="role-reconciliation-dialog-title"
        aria-describedby="role-reconciliation-dialog-description"
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              width: { xs: "calc(100vw - 32px)", sm: "420px", md: "448px" },
              maxWidth: "448px",
              borderRadius: "12px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              padding: { xs: "24px", sm: "28px", md: "32px" },
              overflow: "visible",
            },
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            py: 0,
            px: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "9999px",
              background: "rgba(157, 106, 196, 0.14)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Lock size={24} strokeWidth={1.9} color="#9D6AC4" />
          </Box>
          <DialogTitle
            id="role-reconciliation-dialog-title"
            component="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
              fontWeight: 700,
              textAlign: "center",
              px: 0,
              pt: 0,
              mb: "10px",
              lineHeight: 1.15,
              fontFamily: "Inter, sans-serif",
              background: "linear-gradient(135deg, #9D6AC4 0%, #E7AD87 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Role changed in another tab
          </DialogTitle>
          <DialogContentText id="role-reconciliation-dialog-description">
            This page uses the <strong>{roleLabel(roleReconciliationStore.pageRole)}</strong> view. You are currently
            in <strong>{roleLabel(roleReconciliationStore.stayRole)}</strong> view.
          </DialogContentText>
          <DialogContentText
            sx={{
              fontSize: { xs: "12px", sm: "13px" },
              color: "#666666",
              lineHeight: { xs: 1.5, sm: 1.625 },
              mb: "28px",
              mt: "6px",
              textAlign: "center",
              maxWidth: "360px",
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              opacity: 0.9,
            }}
          >
            Choose how you want to continue. If the selected role cannot access this page, we&apos;ll redirect you to
            that role&apos;s default page.
          </DialogContentText>
          <DialogActions sx={{ px: 0, justifyContent: "center", width: "100%", mt: 0, mb: 0, pb: 0, gap: 1 }}>
            <Button
              onClick={() => handleRoleDialogSelect("stay")}
              variant="outlined"
              sx={{
                height: { xs: "44px", sm: "48px" },
                fontSize: { xs: "16px", sm: "14px" },
                fontWeight: 600,
                fontFamily: "Inter, sans-serif",
                color: "#6B7280",
                borderColor: "#D1D5DB",
                borderRadius: "8px",
                textTransform: "none",
                px: { xs: 1.5, sm: 2 },
              }}
            >
              Stay
            </Button>
            <Button
              onClick={() => handleRoleDialogSelect("page")}
              variant="contained"
              autoFocus
              sx={{
                height: { xs: "44px", sm: "48px" },
                fontSize: { xs: "16px", sm: "14px" },
                fontWeight: 600,
                fontFamily: "Inter, sans-serif",
                background: "#9D6AC4",
                border: "1px solid #9D6AC4",
                color: "#ffffff",
                borderRadius: "8px",
                textTransform: "none",
                px: { xs: 1.5, sm: 2 },
                "&:hover": { background: "#9F88C7", borderColor: "#9F88C7" },
              }}
            >
              Switch
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
});
